// SEO Translations for all pages in multiple languages
export type SEOLanguage = 'en' | 'de' | 'fr' | 'zh-CN' | 'ko';

export interface PageSEO {
  title: string;
  description: string;
  keywords: string;
  ogTitle?: string;
  ogDescription?: string;
}

export interface SEOTranslations {
  home: PageSEO;
  accommodations: PageSEO;
  spa: PageSEO;
  restaurant: PageSEO;
  events: PageSEO;
  localAttractions: PageSEO;
  activities: PageSEO;
  about: PageSEO;
  notFound: PageSEO;
  bestRateGuarantee: PageSEO;
  privacyPolicy: PageSEO;
}

export const seoTranslations: Record<SEOLanguage, SEOTranslations> = {
  // English
  en: {
    home: {
      title: "Beatenberg Resort & Spa near Interlaken | Swiss Alps Luxury",
      description: "Switzerland's purest wellness sanctuary. 15-acre luxury resort in Beatenberg with panoramic Alpine views, NutraSwiss spa programs, and Swiss hospitality.",
      keywords: "Beatenberg, Swiss Alps, luxury resort, wellness spa, Alpine views, Lake Thun, NutraSwiss, Switzerland hotel, mountain resort, spa retreat, Alpine wellness, Swiss hospitality, hotels near Interlaken",
      ogTitle: "Beatenberg Resort & Spa - Swiss Alpine Luxury",
      ogDescription: "Experience Switzerland's purest wellness sanctuary with panoramic Alpine views and world-class spa programs."
    },
    accommodations: {
      title: "Hotel Rooms & Suites near Interlaken | Beatenberg Resort & Spa",
      description: "Luxury rooms and suites with stunning Alpine views. From cozy single rooms to spacious family suites. Book your Swiss mountain retreat at Beatenberg Resort.",
      keywords: "Beatenberg rooms, Swiss hotel rooms, Alpine suites, luxury accommodation, family suite, junior suite, mountain view rooms, Lake Thun hotel, Swiss Alps accommodation, hotels near Interlaken",
      ogTitle: "Luxury Alpine Accommodations - Beatenberg Resort",
      ogDescription: "Discover our luxury rooms and suites with breathtaking Alpine views in the heart of the Swiss mountains."
    },
    spa: {
      title: "Swiss Alpine Spa near Interlaken | Beatenberg Resort & Spa",
      description: "Luxury wellness at Beatenberg Resort & Spa. NutraSwiss programs, Alpine-inspired therapies, and panoramic mountain views in the Swiss Alps.",
      keywords: "Swiss spa, Alpine wellness, NutraSwiss, wellness retreat, spa treatments, mountain spa, luxury wellness, Swiss Alps spa, Beatenberg spa, wellness programs, spa near Interlaken",
      ogTitle: "Swiss Alpine Wellness & Spa - Beatenberg Resort",
      ogDescription: "Experience world-class wellness with NutraSwiss programs and Alpine-inspired treatments in stunning mountain settings."
    },
    restaurant: {
      title: "Restaurants near Interlaken | Beatenberg Resort & Spa",
      description: "Authentic Swiss cuisine with panoramic Alpine views. Farm-to-table dining featuring local ingredients and traditional Swiss specialties at Restaurant Jungfrau.",
      keywords: "Swiss restaurant, Alpine cuisine, farm-to-table, Swiss food, mountain restaurant, Beatenberg dining, Swiss specialties, panoramic views, Restaurant Jungfrau, restaurants near Interlaken",
      ogTitle: "Restaurant Jungfrau - Swiss Alpine Dining Experience",
      ogDescription: "Savor authentic Swiss and international cuisine with breathtaking Alpine views at our panoramic restaurant."
    },
    events: {
      title: "Wedding Venue near Interlaken | Beatenberg Resort & Spa",
      description: "Plan unforgettable events and weddings in the Swiss Alps. Luxury venue with panoramic views, expert planning, and authentic Swiss charm at Beatenberg Resort.",
      keywords: "Swiss wedding venue, Alpine wedding, mountain wedding, corporate events Switzerland, event venue, Beatenberg events, luxury wedding, Swiss Alps celebration, wedding venue Interlaken",
      ogTitle: "Alpine Events & Weddings - Beatenberg Resort",
      ogDescription: "Create magical moments with our stunning Alpine venue, perfect for weddings, corporate events, and celebrations."
    },
    localAttractions: {
      title: "Things to Do near Interlaken | Beatenberg Resort & Spa",
      description: "Discover the best of Swiss Alps from Beatenberg. Jungfraujoch, Interlaken, hiking trails, winter sports, and authentic Swiss experiences near our resort.",
      keywords: "Swiss Alps attractions, Jungfraujoch, Interlaken, Beatenberg activities, Swiss hiking, Alpine experiences, mountain activities, Lake Thun, Niederhorn, Swiss tourism, things to do Interlaken",
      ogTitle: "Swiss Alps Attractions & Activities - Beatenberg",
      ogDescription: "Explore world-famous Swiss attractions, hiking trails, and Alpine adventures from Beatenberg Resort."
    },
    activities: {
      title: "Day Guest Pass near Interlaken | Beatenberg Resort & Spa",
      description: "Experience luxury without overnight stay. CHF 200 Day Guest Pass includes spa, fine dining, sauna, cinema, billiards, and all resort amenities in the Swiss Alps.",
      keywords: "day pass, day guest, spa day pass, resort day pass, Beatenberg activities, Swiss Alps day trip, spa without overnight, luxury day visit, activities near Interlaken",
      ogTitle: "Day Guest Pass - Beatenberg Resort Activities",
      ogDescription: "Everything under one roof: spa, dining, cinema, sports, and Swiss Alpine luxury for day guests."
    },
    about: {
      title: "About Beatenberg Resort & Spa near Interlaken | Swiss Alps",
      description: "Discover the story of Beatenberg Resort & Spa, a luxury Swiss Alpine hotel dedicated to authentic hospitality and wellness excellence since its heritage beginnings.",
      keywords: "Beatenberg Resort, Swiss hotel history, Alpine heritage, Swiss hospitality, luxury resort Switzerland, about Beatenberg, Swiss Alps hotel",
      ogTitle: "Our Story - Beatenberg Resort & Spa",
      ogDescription: "Learn about our commitment to Swiss hospitality excellence and Alpine wellness traditions."
    },
    notFound: {
      title: "Page not found | Beatenberg Resort & Spa",
      description: "This page does not exist or has moved. Return to our Swiss Alps resort homepage or explore wellness and spa.",
      keywords: "404, page not found, Beatenberg Resort",
      ogTitle: "Page not found - Beatenberg Resort & Spa",
      ogDescription: "The page you requested could not be found."
    },
    bestRateGuarantee: {
      title: "Best Rate Guarantee | Beatenberg Resort & Spa",
      description:
        "Book direct on our official website. If you find a lower qualifying OTA rate for the same stay, we match the difference with a credit note — terms and how to claim.",
      keywords:
        "best rate guarantee, lowest price, book direct, Beatenberg, Swiss hotel, OTA price match, credit note",
      ogTitle: "Best Rate Guarantee - Beatenberg Resort & Spa",
      ogDescription:
        "Our direct-booking guarantee: qualifying lower OTA rates matched with a credit note. Submit your claim within 24 hours.",
    },
    privacyPolicy: {
      title: "Privacy Policy | Beatenberg Resort & Spa",
      description:
        "How Beatenberg Resort & Spa handles personal data on our website: collection, use, cookies, retention, and how to contact us.",
      keywords:
        "privacy policy, data protection, Beatenberg, Swiss hotel, GDPR, cookies, personal data",
      ogTitle: "Privacy Policy - Beatenberg Resort & Spa",
      ogDescription:
        "Summary of how we process personal information when you use our website and services.",
    },
  },

  // German (Deutsch)
  de: {
    home: {
      title: "Beatenberg Resort & Spa nahe Interlaken | Schweizer Alpen",
      description: "Schweizer Wellness-Oase der Reinheit. 15-Hektar Luxusresort in Beatenberg mit Alpenpanorama, NutraSwiss Spa-Programmen und Schweizer Gastfreundschaft.",
      keywords: "Beatenberg, Schweizer Alpen, Luxusresort, Wellness Spa, Alpenblick, Thunersee, NutraSwiss, Schweiz Hotel, Berghotel, Spa Retreat, Alpine Wellness, Schweizer Gastfreundschaft, Hotels nahe Interlaken",
      ogTitle: "Beatenberg Resort & Spa - Schweizer Alpenluxus",
      ogDescription: "Erleben Sie die reinste Wellness-Oase der Schweiz mit Alpenpanorama und erstklassigen Spa-Programmen."
    },
    accommodations: {
      title: "Hotels nahe Interlaken | Beatenberg Resort & Spa",
      description: "Luxuszimmer und Suiten mit atemberaubendem Alpenblick. Von gemütlichen Einzelzimmern bis zu geräumigen Familiensuiten. Buchen Sie Ihren Schweizer Bergrückzugsort.",
      keywords: "Beatenberg Zimmer, Schweizer Hotelzimmer, Alpine Suiten, Luxusunterkunft, Familiensuite, Junior Suite, Bergblick Zimmer, Thunersee Hotel, Schweizer Alpen Unterkunft, Hotels nahe Interlaken",
      ogTitle: "Luxuriöse Alpine Unterkünfte - Beatenberg Resort",
      ogDescription: "Entdecken Sie unsere Luxuszimmer und Suiten mit atemberaubendem Alpenblick im Herzen der Schweizer Berge."
    },
    spa: {
      title: "Alpine Wellness nahe Interlaken | Beatenberg Resort & Spa",
      description: "Luxus-Wellness im Beatenberg Resort & Spa. NutraSwiss Programme, von den Alpen inspirierte Therapien und Bergpanorama in den Schweizer Alpen.",
      keywords: "Schweizer Spa, Alpine Wellness, NutraSwiss, Wellness Retreat, Spa Behandlungen, Berg Spa, Luxus Wellness, Schweizer Alpen Spa, Beatenberg Spa, Wellnessprogramme, Spa nahe Interlaken",
      ogTitle: "Schweizer Alpine Wellness & Spa - Beatenberg Resort",
      ogDescription: "Erleben Sie erstklassige Wellness mit NutraSwiss Programmen und von den Alpen inspirierten Behandlungen in atemberaubender Bergkulisse."
    },
    restaurant: {
      title: "Restaurants nahe Interlaken | Beatenberg Resort & Spa",
      description: "Authentische Schweizer Küche mit Alpenpanorama. Farm-to-Table-Dining mit lokalen Zutaten und traditionellen Schweizer Spezialitäten im Restaurant Jungfrau.",
      keywords: "Schweizer Restaurant, Alpine Küche, Farm-to-Table, Schweizer Essen, Bergrestaurant, Beatenberg Gastronomie, Schweizer Spezialitäten, Panoramablick, Restaurant Jungfrau, Restaurants nahe Interlaken",
      ogTitle: "Restaurant Jungfrau - Schweizer Alpines Speiseerlebnis",
      ogDescription: "Genießen Sie authentische Schweizer und internationale Küche mit atemberaubendem Alpenblick in unserem Panorama-Restaurant."
    },
    events: {
      title: "Hochzeitslocation nahe Interlaken | Beatenberg Resort",
      description: "Unvergessliche Events und Hochzeiten in den Schweizer Alpen planen. Luxus-Location mit Panoramablick, Expertenplanung und authentischem Schweizer Charme.",
      keywords: "Schweizer Hochzeitslocation, Alpine Hochzeit, Berghochzeit, Firmenevents Schweiz, Event Location, Beatenberg Events, Luxushochzeit, Schweizer Alpen Feier, Hochzeit Interlaken",
      ogTitle: "Alpine Events & Hochzeiten - Beatenberg Resort",
      ogDescription: "Schaffen Sie magische Momente mit unserer atemberaubenden Alpen-Location, perfekt für Hochzeiten, Firmenevents und Feiern."
    },
    localAttractions: {
      title: "Aktivitäten nahe Interlaken | Beatenberg Resort & Spa",
      description: "Entdecken Sie das Beste der Schweizer Alpen von Beatenberg aus. Jungfraujoch, Interlaken, Wanderwege, Wintersport und authentische Schweizer Erlebnisse.",
      keywords: "Schweizer Alpen Attraktionen, Jungfraujoch, Interlaken, Beatenberg Aktivitäten, Schweizer Wandern, Alpine Erlebnisse, Bergaktivitäten, Thunersee, Niederhorn, Schweiz Tourismus",
      ogTitle: "Schweizer Alpen Attraktionen & Aktivitäten - Beatenberg",
      ogDescription: "Erkunden Sie weltberühmte Schweizer Attraktionen, Wanderwege und alpine Abenteuer vom Beatenberg Resort aus."
    },
    activities: {
      title: "Tagsgast Pass nahe Interlaken | Beatenberg Resort & Spa",
      description: "Luxus ohne Übernachtung erleben. CHF 200 Tagesgast-Pass inkl. Spa, Fine Dining, Sauna, Kino, Billard und alle Resort-Annehmlichkeiten in den Schweizer Alpen.",
      keywords: "Tagespass, Tagesgast, Spa Tagespass, Resort Tagespass, Beatenberg Aktivitäten, Schweizer Alpen Tagesausflug, Spa ohne Übernachtung, Luxus Tagesbesuch",
      ogTitle: "Tagesgast Pass - Beatenberg Resort Aktivitäten",
      ogDescription: "Alles unter einem Dach: Spa, Gastronomie, Kino, Sport und Schweizer Alpenluxus für Tagesgäste."
    },
    about: {
      title: "Über Beatenberg Resort & Spa nahe Interlaken",
      description: "Entdecken Sie die Geschichte des Beatenberg Resort & Spa, einem Luxus-Schweizer Alpenhotel, das sich seit seinem historischen Beginn authentischer Gastfreundschaft und Wellness-Exzellenz verschrieben hat.",
      keywords: "Beatenberg Resort, Schweizer Hotel Geschichte, Alpen-Erbe, Schweizer Gastfreundschaft, Luxusresort Schweiz, über Beatenberg, Schweizer Alpen Hotel",
      ogTitle: "Unsere Geschichte - Beatenberg Resort & Spa",
      ogDescription: "Erfahren Sie mehr über unser Engagement für Schweizer Gastfreundschaftsexzellenz und Alpine Wellness-Traditionen."
    },
    notFound: {
      title: "Seite nicht gefunden | Beatenberg Resort & Spa",
      description: "Diese Seite existiert nicht oder wurde verschoben. Zurück zur Startseite oder zu Wellness & Spa.",
      keywords: "404, Seite nicht gefunden, Beatenberg Resort",
      ogTitle: "Seite nicht gefunden - Beatenberg Resort & Spa",
      ogDescription: "Die angeforderte Seite konnte nicht gefunden werden."
    },
    bestRateGuarantee: {
      title: "Bestpreisgarantie | Beatenberg Resort & Spa",
      description:
        "Direkt auf unserer offiziellen Website buchen. Finden Sie einen niedrigeren qualifizierten OTA-Tarif für denselben Aufenthalt, gleichen wir die Differenz mit einer Gutschrift aus — Bedingungen und Einreichung.",
      keywords:
        "Bestpreisgarantie, günstigster Preis, direkt buchen, Beatenberg, Schweizer Hotel, OTA Preisgarantie, Gutschrift",
      ogTitle: "Bestpreisgarantie - Beatenberg Resort & Spa",
      ogDescription:
        "Garantie für Direktbuchungen: qualifizierte niedrigere OTA-Tarife werden mit einer Gutschrift ausgeglichen. Einreichung innerhalb von 24 Stunden.",
    },
    privacyPolicy: {
      title: "Datenschutz | Beatenberg Resort & Spa",
      description:
        "Wie das Beatenberg Resort & Spa personenbezogene Daten auf unserer Website verarbeitet: Erhebung, Verwendung, Cookies, Aufbewahrung und Kontakt.",
      keywords:
        "Datenschutz, Datenschutzerklärung, Beatenberg, Schweizer Hotel, DSGVO, Cookies",
      ogTitle: "Datenschutz - Beatenberg Resort & Spa",
      ogDescription:
        "Überblick über die Verarbeitung personenbezogener Daten bei Nutzung unserer Website und Dienste.",
    },
  },

  // French (Français)
  fr: {
    home: {
      title: "Beatenberg Resort & Spa près d'Interlaken | Alpes Suisses",
      description: "Sanctuaire de bien-être suisse le plus pur. Resort de luxe de 15 acres à Beatenberg avec vues panoramiques alpines, programmes spa NutraSwiss et hospitalité suisse.",
      keywords: "Beatenberg, Alpes suisses, resort de luxe, spa bien-être, vues alpines, Lac de Thoune, NutraSwiss, hôtel Suisse, resort de montagne, retraite spa, bien-être alpin, hospitalité suisse, hôtels près Interlaken",
      ogTitle: "Beatenberg Resort & Spa - Luxe Alpin Suisse",
      ogDescription: "Découvrez le sanctuaire de bien-être le plus pur de Suisse avec vues alpines panoramiques et programmes spa de classe mondiale."
    },
    accommodations: {
      title: "Hôtels près d'Interlaken | Beatenberg Resort & Spa",
      description: "Chambres et suites de luxe avec vues alpines époustouflantes. Des chambres simples confortables aux suites familiales spacieuses. Réservez votre retraite montagnarde suisse.",
      keywords: "Chambres Beatenberg, chambres hôtel suisse, suites alpines, hébergement luxe, suite familiale, suite junior, chambres vue montagne, hôtel Lac de Thoune, hébergement Alpes suisses, hôtels près Interlaken",
      ogTitle: "Hébergements Alpins de Luxe - Beatenberg Resort",
      ogDescription: "Découvrez nos chambres et suites de luxe avec vues alpines à couper le souffle au cœur des montagnes suisses."
    },
    spa: {
      title: "Spa Alpin près d'Interlaken | Beatenberg Resort & Spa",
      description: "Bien-être de luxe au Beatenberg Resort & Spa. Programmes NutraSwiss, thérapies d'inspiration alpine et vues panoramiques sur les montagnes dans les Alpes suisses.",
      keywords: "Spa suisse, bien-être alpin, NutraSwiss, retraite bien-être, soins spa, spa de montagne, bien-être luxe, spa Alpes suisses, spa Beatenberg, programmes bien-être, spa près Interlaken",
      ogTitle: "Bien-être & Spa Alpin Suisse - Beatenberg Resort",
      ogDescription: "Vivez un bien-être de classe mondiale avec les programmes NutraSwiss et des soins d'inspiration alpine dans un cadre montagnard époustouflant."
    },
    restaurant: {
      title: "Restaurants près d'Interlaken | Beatenberg Resort & Spa",
      description: "Cuisine suisse authentique avec vues alpines panoramiques. Gastronomie de la ferme à la table avec ingrédients locaux et spécialités suisses traditionnelles au Restaurant Jungfrau.",
      keywords: "Restaurant suisse, cuisine alpine, ferme à table, cuisine suisse, restaurant de montagne, gastronomie Beatenberg, spécialités suisses, vues panoramiques, Restaurant Jungfrau, restaurants près Interlaken",
      ogTitle: "Restaurant Jungfrau - Expérience Gastronomique Alpine Suisse",
      ogDescription: "Savourez une cuisine suisse et internationale authentique avec vues alpines époustouflantes dans notre restaurant panoramique."
    },
    events: {
      title: "Lieu Mariage près d'Interlaken | Beatenberg Resort & Spa",
      description: "Planifiez des événements et mariages inoubliables dans les Alpes suisses. Lieu de luxe avec vues panoramiques, planification experte et charme suisse authentique.",
      keywords: "Lieu mariage suisse, mariage alpin, mariage montagne, événements d'entreprise Suisse, lieu événement, événements Beatenberg, mariage luxe, célébration Alpes suisses, mariage Interlaken",
      ogTitle: "Événements & Mariages Alpins - Beatenberg Resort",
      ogDescription: "Créez des moments magiques avec notre lieu alpin époustouflant, parfait pour les mariages, événements d'entreprise et célébrations."
    },
    localAttractions: {
      title: "Activités près d'Interlaken | Beatenberg Resort & Spa",
      description: "Découvrez le meilleur des Alpes suisses depuis Beatenberg. Jungfraujoch, Interlaken, sentiers de randonnée, sports d'hiver et expériences suisses authentiques près de notre resort.",
      keywords: "Attractions Alpes suisses, Jungfraujoch, Interlaken, activités Beatenberg, randonnée suisse, expériences alpines, activités montagne, Lac de Thoune, Niederhorn, tourisme suisse",
      ogTitle: "Attractions & Activités des Alpes Suisses - Beatenberg",
      ogDescription: "Explorez les attractions suisses mondialement connues, sentiers de randonnée et aventures alpines depuis le Beatenberg Resort."
    },
    activities: {
      title: "Pass Visiteur Journée près d'Interlaken | Beatenberg Resort",
      description: "Luxe sans nuitée. Pass journée CHF 200 inclut spa, gastronomie, sauna, cinéma, billard et tous les équipements du resort dans les Alpes suisses.",
      keywords: "pass journée, visiteur jour, pass spa journée, pass resort journée, activités Beatenberg, Alpes suisses excursion jour, spa sans nuitée, visite luxe journée",
      ogTitle: "Pass Visiteur Journée - Activités Beatenberg Resort",
      ogDescription: "Tout sous un même toit : spa, gastronomie, cinéma, sports et luxe alpin suisse pour visiteurs journée."
    },
    about: {
      title: "À Propos de Beatenberg Resort & Spa près d'Interlaken",
      description: "Découvrez l'histoire du Beatenberg Resort & Spa, un hôtel alpin suisse de luxe dédié à l'hospitalité authentique et à l'excellence du bien-être depuis ses débuts patrimoniaux.",
      keywords: "Beatenberg Resort, histoire hôtel suisse, patrimoine alpin, hospitalité suisse, resort de luxe Suisse, à propos Beatenberg, hôtel Alpes suisses",
      ogTitle: "Notre Histoire - Beatenberg Resort & Spa",
      ogDescription: "Découvrez notre engagement envers l'excellence de l'hospitalité suisse et les traditions du bien-être alpin."
    },
    notFound: {
      title: "Page introuvable | Beatenberg Resort & Spa",
      description: "Cette page n'existe pas ou a été déplacée. Retour à l'accueil ou découverte du spa et du bien-être.",
      keywords: "404, page introuvable, Beatenberg Resort",
      ogTitle: "Page introuvable - Beatenberg Resort & Spa",
      ogDescription: "La page demandée est introuvable."
    },
    bestRateGuarantee: {
      title: "Garantie du meilleur tarif | Beatenberg Resort & Spa",
      description:
        "Réservez sur notre site officiel. Tarif OTA public admissible plus bas pour le même séjour ? Nous compensons la différence par un avoir — conditions et démarche.",
      keywords:
        "garantie meilleur tarif, prix le plus bas, réservation directe, Beatenberg, hôtel suisse, OTA, avoir",
      ogTitle: "Garantie du meilleur tarif - Beatenberg Resort & Spa",
      ogDescription:
        "Notre garantie pour les réservations directes : différence sur un tarif OTA admissible remboursée sous forme d’avoir. Réclamation sous 24 h.",
    },
    privacyPolicy: {
      title: "Politique de confidentialité | Beatenberg Resort & Spa",
      description:
        "Comment le Beatenberg Resort & Spa traite les données personnelles sur notre site : collecte, utilisation, cookies, conservation et contact.",
      keywords:
        "politique de confidentialité, protection des données, Beatenberg, hôtel suisse, RGPD, cookies",
      ogTitle: "Politique de confidentialité - Beatenberg Resort & Spa",
      ogDescription:
        "Résumé du traitement des données personnelles lors de l’utilisation de notre site et de nos services.",
    },
  },

  // Simplified Chinese (简体中文)
  'zh-CN': {
    home: {
      title: "碧滕贝格度假村及水疗中心 - 瑞士阿尔卑斯山巅峰之选",
      description: "瑞士最纯净的养生圣地。15英亩奢华度假村，坐落于碧滕贝格，享有阿尔卑斯山全景、NutraSwiss水疗项目和瑞士式盛情款待。",
      keywords: "碧滕贝格, 瑞士阿尔卑斯, 豪华度假村, 养生水疗, 阿尔卑斯景观, 图恩湖, NutraSwiss, 瑞士酒店, 山地度假村, 水疗休养, 阿尔卑斯养生, 瑞士款待",
      ogTitle: "碧滕贝格度假村及水疗中心 - 瑞士阿尔卑斯奢华体验",
      ogDescription: "体验瑞士最纯净的养生圣地，享受阿尔卑斯山全景和世界级水疗项目。"
    },
    accommodations: {
      title: "客房套房 - 碧滕贝格度假村及水疗中心 | 阿尔卑斯豪华住宿",
      description: "豪华客房和套房，享有壮丽阿尔卑斯山景。从舒适单人间到宽敞家庭套房。预订您的瑞士山地静修之旅。",
      keywords: "碧滕贝格客房, 瑞士酒店客房, 阿尔卑斯套房, 豪华住宿, 家庭套房, 初级套房, 山景客房, 图恩湖酒店, 瑞士阿尔卑斯住宿",
      ogTitle: "阿尔卑斯豪华住宿 - 碧滕贝格度假村",
      ogDescription: "探索我们的豪华客房和套房，在瑞士山脉中心享受令人叹为观止的阿尔卑斯山景。"
    },
    spa: {
      title: "养生水疗 - 碧滕贝格度假村及水疗中心 | 瑞士阿尔卑斯养生",
      description: "碧滕贝格度假村及水疗中心的奢华养生体验。NutraSwiss项目、阿尔卑斯风格疗法，瑞士阿尔卑斯山全景环绕。",
      keywords: "瑞士水疗, 阿尔卑斯养生, NutraSwiss, 养生休养, 水疗护理, 山地水疗, 奢华养生, 瑞士阿尔卑斯水疗, 碧滕贝格水疗, 养生项目",
      ogTitle: "瑞士阿尔卑斯养生水疗 - 碧滕贝格度假村",
      ogDescription: "体验世界级养生，享受NutraSwiss项目和阿尔卑斯风格护理，置身壮丽山景之中。"
    },
    restaurant: {
      title: "餐饮美食 - 碧滕贝格度假村及水疗中心 | 瑞士阿尔卑斯美食",
      description: "正宗瑞士美食配阿尔卑斯山全景。农场直送餐桌，采用本地食材和传统瑞士特色菜肴，尽在容夫劳餐厅。",
      keywords: "瑞士餐厅, 阿尔卑斯美食, 农场直送, 瑞士美食, 山地餐厅, 碧滕贝格餐饮, 瑞士特色菜, 全景视野, 容夫劳餐厅",
      ogTitle: "容夫劳餐厅 - 瑞士阿尔卑斯美食体验",
      ogDescription: "在我们的全景餐厅品尝正宗瑞士和国际美食，享受令人叹为观止的阿尔卑斯山景。"
    },
    events: {
      title: "活动婚礼 - 碧滕贝格度假村及水疗中心 | 瑞士阿尔卑斯场地",
      description: "在瑞士阿尔卑斯山规划难忘的活动和婚礼。豪华场地配全景视野、专业策划和正宗瑞士魅力。",
      keywords: "瑞士婚礼场地, 阿尔卑斯婚礼, 山地婚礼, 瑞士企业活动, 活动场地, 碧滕贝格活动, 奢华婚礼, 瑞士阿尔卑斯庆典",
      ogTitle: "阿尔卑斯活动婚礼 - 碧滕贝格度假村",
      ogDescription: "在我们壮丽的阿尔卑斯场地创造魔幻时刻，完美适合婚礼、企业活动和庆典。"
    },
    localAttractions: {
      title: "当地景点体验 - 碧滕贝格度假村及水疗中心",
      description: "从碧滕贝格探索瑞士阿尔卑斯山精华。少女峰、因特拉肯、徒步小径、冬季运动和正宗瑞士体验，尽在我们度假村附近。",
      keywords: "瑞士阿尔卑斯景点, 少女峰, 因特拉肯, 碧滕贝格活动, 瑞士徒步, 阿尔卑斯体验, 山地活动, 图恩湖, 尼德峰, 瑞士旅游",
      ogTitle: "瑞士阿尔卑斯景点活动 - 碧滕贝格",
      ogDescription: "从碧滕贝格度假村探索世界著名的瑞士景点、徒步小径和阿尔卑斯探险。"
    },
    activities: {
      title: "日间访客通行证 - 碧滕贝格度假村及水疗中心",
      description: "无需过夜的奢华体验。200瑞士法郎日间访客通行证包含水疗、精致餐饮、桑拿、电影院、台球及瑞士阿尔卑斯度假村所有设施。",
      keywords: "日间通行证, 日间访客, 水疗日间通行证, 度假村日间通行证, 碧滕贝格活动, 瑞士阿尔卑斯一日游, 无过夜水疗, 奢华日访",
      ogTitle: "日间访客通行证 - 碧滕贝格度假村活动",
      ogDescription: "一站式体验：水疗、餐饮、电影院、运动和瑞士阿尔卑斯奢华，专为日间访客设计。"
    },
    about: {
      title: "关于我们 - 碧滕贝格度假村及水疗中心 | 瑞士阿尔卑斯传承",
      description: "探索碧滕贝格度假村及水疗中心的故事，一家瑞士阿尔卑斯豪华酒店，自其传承开端便致力于正宗款待和养生卓越。",
      keywords: "碧滕贝格度假村, 瑞士酒店历史, 阿尔卑斯传承, 瑞士款待, 瑞士豪华度假村, 关于碧滕贝格, 瑞士阿尔卑斯酒店",
      ogTitle: "我们的故事 - 碧滕贝格度假村及水疗中心",
      ogDescription: "了解我们对瑞士款待卓越和阿尔卑斯养生传统的承诺。"
    },
    notFound: {
      title: "页面未找到 | 碧滕贝格度假村及水疗中心",
      description: "该页面不存在或已移动。请返回瑞士阿尔卑斯度假村主页或浏览水疗与养生。",
      keywords: "404, 页面未找到, 碧滕贝格度假村",
      ogTitle: "页面未找到 - 碧滕贝格度假村及水疗中心",
      ogDescription: "无法找到您请求的页面。"
    },
    bestRateGuarantee: {
      title: "最优价格保证 | 碧滕贝格度假村及水疗中心",
      description:
        "在官网预订并预付。若在同一住宿期间发现符合条件的更低OTA公开价，我们将以抵用额度补足差价——条款与申请方式详见页面。",
      keywords: "最优价格保证, 官网预订, 碧滕贝格, 瑞士酒店, OTA, 抵用券",
      ogTitle: "最优价格保证 - 碧滕贝格度假村及水疗中心",
      ogDescription: "官网直订保障：符合条件的更低OTA房价将以抵用额度形式匹配。须在预订后24小时内提交申请。",
    },
    privacyPolicy: {
      title: "隐私政策 | 碧滕贝格度假村及水疗中心",
      description:
        "碧滕贝格度假村及水疗中心网站如何处理个人信息：收集、使用、Cookie、保存期限与联系方式。",
      keywords: "隐私政策, 数据保护, 碧滕贝格, 瑞士酒店, Cookie, 个人信息",
      ogTitle: "隐私政策 - 碧滕贝格度假村及水疗中心",
      ogDescription: "使用本网站与服务时我们如何处理个人信息的概要说明。",
    },
  },

  // Korean (한국어)
  'ko': {
    home: {
      title: "베아텐베르크 리조트 & 스파 - 최고의 스위스 알프스",
      description: "스위스의 가장 순수한 웰니스 성지. 베아텐베르크의 15에이커 럭셔리 리조트, 알프스 파노라마 뷰, NutraSwiss 스파 프로그램과 스위스 환대.",
      keywords: "베아텐베르크, 스위스 알프스, 럭셔리 리조트, 웰니스 스파, 알프스 전망, 툰 호수, NutraSwiss, 스위스 호텔, 산악 리조트, 스파 휴양지, 알파인 웰니스, 스위스 환대",
      ogTitle: "베아텐베르크 리조트 & 스파 - 스위스 알프스 럭셔리",
      ogDescription: "파노라마 알프스 전망과 세계적인 스파 프로그램을 갖춘 스위스의 가장 순수한 웰니스 성지를 경험하세요."
    },
    accommodations: {
      title: "객실 & 스위트 - 베아텐베르크 리조트 & 스파 | 알파인 럭셔리 숙박",
      description: "멋진 알프스 전망을 갖춘 럭셔리 객실과 스위트. 아늑한 싱글룸부터 넓은 패밀리 스위트까지. 스위스 산악 휴양지를 예약하세요.",
      keywords: "베아텐베르크 객실, 스위스 호텔 객실, 알파인 스위트, 럭셔리 숙박, 패밀리 스위트, 주니어 스위트, 마운틴 뷰 객실, 툰 호수 호텔, 스위스 알프스 숙박",
      ogTitle: "럭셔리 알파인 숙박 - 베아텐베르크 리조트",
      ogDescription: "스위스 산맥 중심부에서 숨막히는 알프스 전망을 갖춘 럭셔리 객실과 스위트를 만나보세요."
    },
    spa: {
      title: "웰니스 & 스파 - 베아텐베르크 리조트 & 스파 | 스위스 알파인 웰니스",
      description: "베아텐베르크 리조트 & 스파의 럭셔리 웰니스. NutraSwiss 프로그램, 알프스 테라피, 스위스 알프스의 파노라마 산악 전망.",
      keywords: "스위스 스파, 알파인 웰니스, NutraSwiss, 웰니스 휴양지, 스파 트리트먼트, 마운틴 스파, 럭셔리 웰니스, 스위스 알프스 스파, 베아텐베르크 스파, 웰니스 프로그램",
      ogTitle: "스위스 알파인 웰니스 & 스파 - 베아텐베르크 리조트",
      ogDescription: "멋진 산악 환경에서 NutraSwiss 프로그램과 알프스 테라피로 세계적인 웰니스를 경험하세요."
    },
    restaurant: {
      title: "다이닝 & 레스토랑 - 베아텐베르크 리조트 & 스파 | 스위스 알파인 요리",
      description: "파노라마 알프스 전망과 함께하는 정통 스위스 요리. 레스토랑 융프라우에서 현지 식재료와 전통 스위스 특선 요리로 만든 팜투테이블 다이닝.",
      keywords: "스위스 레스토랑, 알파인 요리, 팜투테이블, 스위스 음식, 산악 레스토랑, 베아텐베르크 다이닝, 스위스 특선 요리, 파노라마 뷰, 레스토랑 융프라우",
      ogTitle: "레스토랑 융프라우 - 스위스 알파인 다이닝 경험",
      ogDescription: "숨막히는 알프스 전망과 함께 파노라마 레스토랑에서 정통 스위스 및 세계 요리를 즐기세요."
    },
    events: {
      title: "이벤트 & 웨딩 - 베아텐베르크 리조트 & 스파 | 스위스 알프스 베뉴",
      description: "스위스 알프스에서 잊을 수 없는 이벤트와 웨딩을 계획하세요. 파노라마 뷰, 전문 기획, 정통 스위스 매력을 갖춘 럭셔리 베뉴.",
      keywords: "스위스 웨딩 베뉴, 알파인 웨딩, 산악 웨딩, 스위스 기업 행사, 이벤트 베뉴, 베아텐베르크 이벤트, 럭셔리 웨딩, 스위스 알프스 축하",
      ogTitle: "알파인 이벤트 & 웨딩 - 베아텐베르크 리조트",
      ogDescription: "웨딩, 기업 행사, 축하 행사에 완벽한 멋진 알프스 베뉴로 마법 같은 순간을 만드세요."
    },
    localAttractions: {
      title: "현지 명소 & 체험 - 베아텐베르크 리조트 & 스파",
      description: "베아텐베르크에서 스위스 알프스 최고를 발견하세요. 융프라우요흐, 인터라켄, 하이킹 코스, 겨울 스포츠, 리조트 근처의 정통 스위스 체험.",
      keywords: "스위스 알프스 명소, 융프라우요흐, 인터라켄, 베아텐베르크 액티비티, 스위스 하이킹, 알파인 체험, 산악 활동, 툰 호수, 니더호른, 스위스 관광",
      ogTitle: "스위스 알프스 명소 & 액티비티 - 베아텐베르크",
      ogDescription: "베아텐베르크 리조트에서 세계적으로 유명한 스위스 명소, 하이킹 코스, 알파인 모험을 탐험하세요."
    },
    activities: {
      title: "데이 게스트 패스 - 베아텐베르크 리조트 & 스파",
      description: "숙박 없이 럭셔리를 경험하세요. 200 스위스 프랑 데이 게스트 패스는 스파, 파인 다이닝, 사우나, 영화관, 당구, 스위스 알프스 리조트 모든 시설 포함.",
      keywords: "데이 패스, 일일 게스트, 스파 데이 패스, 리조트 데이 패스, 베아텐베르크 액티비티, 스위스 알프스 당일치기, 숙박 없는 스파, 럭셔리 일일 방문",
      ogTitle: "데이 게스트 패스 - 베아텐베르크 리조트 액티비티",
      ogDescription: "한 곳에서 모두: 스파, 다이닝, 영화관, 스포츠, 그리고 데이 게스트를 위한 스위스 알파인 럭셔리."
    },
    about: {
      title: "소개 - 베아텐베르크 리조트 & 스파 | 스위스 알파인 유산",
      description: "유산의 시작부터 정통 환대와 웰니스 우수성에 전념해 온 럭셔리 스위스 알파인 호텔, 베아텐베르크 리조트 & 스파의 이야기를 발견하세요.",
      keywords: "베아텐베르크 리조트, 스위스 호텔 역사, 알파인 유산, 스위스 환대, 럭셔리 리조트 스위스, 베아텐베르크 소개, 스위스 알프스 호텔",
      ogTitle: "우리의 이야기 - 베아텐베르크 리조트 & 스파",
      ogDescription: "스위스 환대 우수성과 알파인 웰니스 전통에 대한 우리의 헌신을 알아보세요."
    },
    notFound: {
      title: "페이지를 찾을 수 없음 | 베아텐베르크 리조트 & 스파",
      description: "페이지가 없거나 이동되었습니다. 스위스 알프스 리조트 홈으로 돌아가거나 스파를 둘러보세요.",
      keywords: "404, 페이지 없음, 베아텐베르크 리조트",
      ogTitle: "페이지를 찾을 수 없음 - 베아텐베르크 리조트 & 스파",
      ogDescription: "요청하신 페이지를 찾을 수 없습니다."
    },
    bestRateGuarantee: {
      title: "최저가 보장 | 베아텐베르크 리조트 & 스파",
      description:
        "공식 웹사이트에서 예약·선결제. 동일 숙박에 대해 조건을 충족하는 더 낮은 OTA 요금을 찾으면 차액을 크레딧 노트로 보전합니다. 약관 및 신청 방법은 페이지를 참고하세요.",
      keywords: "최저가 보장, 직접 예약, 베아텐베르크, 스위스 호텔, OTA, 크레딧",
      ogTitle: "최저가 보장 - 베아텐베르크 리조트 & 스파",
      ogDescription:
        "직접 예약 보장: 조건을 충족하는 더 낮은 OTA 객실 요금은 크레딧 노트로 차액을 맞춥니다. 예약 후 24시간 이내 신청.",
    },
    privacyPolicy: {
      title: "개인정보 처리방침 | 베아텐베르크 리조트 & 스파",
      description:
        "웹사이트 이용 시 베아텐베르크 리조트 & 스파의 개인정보 수집·이용, 쿠키, 보관 및 문의 방법에 대한 안내입니다.",
      keywords: "개인정보, 처리방침, 베아텐베르크, 스위스 호텔, 쿠키",
      ogTitle: "개인정보 처리방침 - 베아텐베르크 리조트 & 스파",
      ogDescription: "웹사이트 및 온라인 서비스 이용 시 개인정보 처리에 대한 요약입니다.",
    },
  }
};

// Helper function to get SEO data for a specific page and language
export const getPageSEO = (page: keyof SEOTranslations, language: SEOLanguage = 'en'): PageSEO => {
  return seoTranslations[language][page];
};

// Helper function to get all available languages
export const getAvailableLanguages = (): SEOLanguage[] => {
  // Only return languages with FULL content translations
  // TODO: Add 'zh-CN' and 'ko' when UI content is translated
  return ['en', 'de', 'fr'];
  
  // Available but disabled until full translation:
  // return ['en', 'de', 'fr', 'zh-CN', 'ko'];
};

// Helper function to get language name
export const getLanguageName = (lang: SEOLanguage): string => {
  const names: Record<SEOLanguage, string> = {
    'en': 'English',
    'de': 'Deutsch',
    'fr': 'Français',
    'zh-CN': '简体中文',
    'ko': '한국어'
  };
  return names[lang];
};

// Helper function to get alternate URLs for hreflang
export const getAlternateUrls = (path: string, baseUrl: string = 'https://beatenberg-resortspa.ch'): Record<SEOLanguage | 'x-default', string> => {
  const languages = getAvailableLanguages();
  const alternates: any = {};
  
  languages.forEach(lang => {
    // Use path-based routing: English has no prefix, German/French use /de/ and /fr/
    if (lang === 'en') {
      alternates[lang] = `${baseUrl}${path}`;
    } else {
      alternates[lang] = `${baseUrl}/${lang}${path}`;
    }
  });
  
  // x-default points to English version
  alternates['x-default'] = `${baseUrl}${path}`;
  
  return alternates;
};

